import RegularText from '@/components/RegularText';
import { DEFAULT_CUT_OFF_TIME, DEFAULT_PREPARATION_TIME, DEFAULT_WEEKLY_WORKING_DATA, NUMBER_REGEX } from '@/constants';
import { KindOfTimeType, StatusOptions, TimeTypeTabs, TimeUnitOptions, WeekdayEnum } from '@/constants/enum';
import { isEmpty } from '@/helpers';
import {
  handleSavePickupSettings,
  handleSavePickupWeeklyWorkingDays,
  pickupSettingSelector,
  pickupWeeklyWorkingDaySelector,
} from '@/redux/slice/pickup.slice';
import { ITimeUnit } from '@/types';
import { IWeeklyWorkingDay } from '@/types/components/day';

import { time24hTo12h } from '@/utils';
import {
  BlockStack,
  Box,
  Checkbox,
  FormLayout,
  Icon,
  InlineGrid,
  InlineStack,
  Select,
  Tabs,
  TextField,
  Tooltip,
} from '@shopify/polaris';
import { InfoIcon } from '@shopify/polaris-icons';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

export type TSelectTabDays = {
  selectTabDays: number;
  setSelectTabDays: React.Dispatch<React.SetStateAction<number>>;
  selectedWorkingDay: IWeeklyWorkingDay;
  workingDayTabs: WorkingDayTab[];
  time24h: { id: number; value: string }[];
};

type WorkingDayTab = {
  id: string;
  content: string;
  panelID: string;
};

const PickupPrepareTime = ({ selectTabDays, setSelectTabDays, selectedWorkingDay, workingDayTabs, time24h }: TSelectTabDays) => {
  const { t } = useTranslation('pickupSettings');
  const dispatch = useDispatch();
  const pickupSettings = useSelector(pickupSettingSelector);
  const pickupWeeklyWorkingDays = useSelector(pickupWeeklyWorkingDaySelector);
  const preparationTimeTypeTabSelected = useMemo(() => {
    return pickupSettings.pickupPreparationTimeType === KindOfTimeType.SAME ? TimeTypeTabs.SAME : TimeTypeTabs.DIFFERENT;
  }, [pickupSettings.pickupPreparationTimeType]);
  const [dayToCopy, setDayToCopy] = useState<string>('');
  const handleTabDayChange = (value: number) => {
    setSelectTabDays(value);
    setDayToCopy('');
  };

  const pickupDayLabels = useMemo(() => {
    return [
      t('weekDaysLong.0'), //sunday
      t('weekDaysLong.1'), //monday
      t('weekDaysLong.2'), //tuesday
      t('weekDaysLong.3'), //wednesday
      t('weekDaysLong.4'), //thursday
      t('weekDaysLong.5'), //friday
      t('weekDaysLong.6'), //saturday
    ];
  }, [t]);

  const statusOptions = useMemo(() => {
    return [
      { label: t('statusSettings.enable'), value: StatusOptions.ENABLE },
      { label: t('statusSettings.disable'), value: StatusOptions.DISABLE },
    ];
  }, [t]);
  const timeUnitOptions = useMemo(() => {
    return [
      { label: t('visiblePickupDays.pickupPrepareTime.minutes'), value: TimeUnitOptions.MINUTE },
      { label: t('visiblePickupDays.pickupPrepareTime.hours'), value: TimeUnitOptions.HOUR },
      { label: t('visiblePickupDays.pickupPrepareTime.days'), value: TimeUnitOptions.DAY },
    ];
  }, [t]);

  const copyOptionList = useMemo(() => {
    return [
      { label: t('pickupTime.exactTime.copySection.select.options.monday'), value: WeekdayEnum.MONDAY.toString() },
      { label: t('pickupTime.exactTime.copySection.select.options.tuesday'), value: WeekdayEnum.TUESDAY.toString() },
      { label: t('pickupTime.exactTime.copySection.select.options.wednesday'), value: WeekdayEnum.WEDNESDAY.toString() },
      { label: t('pickupTime.exactTime.copySection.select.options.thursday'), value: WeekdayEnum.THURSDAY.toString() },
      { label: t('pickupTime.exactTime.copySection.select.options.friday'), value: WeekdayEnum.FRIDAY.toString() },
      { label: t('pickupTime.exactTime.copySection.select.options.saturday'), value: WeekdayEnum.SATURDAY.toString() },
      { label: t('pickupTime.exactTime.copySection.select.options.sunday'), value: WeekdayEnum.SUNDAY.toString() },
    ]
      .filter((item) => DEFAULT_WEEKLY_WORKING_DATA.find((day) => day.day === parseInt(item.value, 10))?.isEnable)
      .filter((item) => item.value !== selectedWorkingDay?.day.toString());
  }, [selectedWorkingDay?.day, t]);

  const timeOptions = useMemo(() => {
    return time24h.map((item) => ({
      key: item.value,
      label: time24hTo12h(item.value),
      value: item.value,
    }));
  }, [time24h]);

  const pickupTimeTabs = useMemo(
    () => [
      {
        id: `pickup-ID-${KindOfTimeType.SAME}`,
        content: t('visiblePickupDays.pickupPrepareTime.basic'),
        panelID: `pickup-panelID-${KindOfTimeType.SAME}`,
      },
      {
        id: `pickup-ID-${KindOfTimeType.DIFFERENT}`,
        content: t('visiblePickupDays.pickupPrepareTime.advanced'),
        panelID: `pickup-panelID-${KindOfTimeType.DIFFERENT}`,
      },
    ],
    [t],
  );

  const handleChangeCopySetup = (selected: string) => {
    setDayToCopy(selected);
  };

  const handleCopy = (value: string) => {
    handleChangeCopySetup(value);
    const day = parseInt(value);
    const dayToCopyData = pickupWeeklyWorkingDays.find((item) => item.day === day);

    if (dayToCopyData) {
      dispatch(
        handleSavePickupWeeklyWorkingDays(
          pickupWeeklyWorkingDays.map((item) => {
            if (item.day === selectedWorkingDay?.day) {
              return {
                ...item,
                preparationTime: dayToCopyData.preparationTime,
                isEnableCutOffTime: dayToCopyData.isEnableCutOffTime,
                cutOffTime: dayToCopyData.cutOffTime,
                cutOffPreparationTime: dayToCopyData.cutOffPreparationTime,
                dateRange: dayToCopyData.dateRange,
              };
            }
            return item;
          }),
        ),
      );
    }
  };

  const handleSelectPickupTime = (selectedTabIndex: number) => {
    if (selectedTabIndex === TimeTypeTabs.SAME) {
      dispatch(handleSavePickupSettings({ pickupPreparationTimeType: KindOfTimeType.SAME }));
    } else dispatch(handleSavePickupSettings({ pickupPreparationTimeType: KindOfTimeType.DIFFERENT }));
  };

  return (
    <BlockStack gap="200">
      <Select
        label={
          <InlineStack blockAlign="center" gap={'150'}>
            <RegularText>{t('visiblePickupDays.pickupPrepareTime.preparationLabel')}</RegularText>
            <Tooltip content={t('visiblePickupDays.pickupPrepareTime.preparationInstruction')} width="wide">
              <Box>
                <Icon source={InfoIcon} />
              </Box>
            </Tooltip>
          </InlineStack>
        }
        options={statusOptions}
        value={pickupSettings.isEnablePreparationTime ? StatusOptions.ENABLE : StatusOptions.DISABLE}
        onChange={(value) => {
          dispatch(handleSavePickupSettings({ isEnablePreparationTime: value === StatusOptions.ENABLE }));
        }}
      />
      {pickupSettings.isEnablePreparationTime && (
        <BlockStack gap="200">
          <Tabs tabs={pickupTimeTabs} selected={preparationTimeTypeTabSelected} onSelect={handleSelectPickupTime} fitted />
          {pickupSettings.pickupPreparationTimeType === KindOfTimeType.DIFFERENT && (
            <>
              <Tabs tabs={workingDayTabs} selected={selectTabDays} onSelect={handleTabDayChange} fitted></Tabs>
              <InlineGrid gap="100" columns="1fr auto">
                <Select
                  label={t('visiblePickupDays.pickupPrepareTime.copySetup')}
                  placeholder={t('visiblePickupDays.pickupPrepareTime.copyPlaceholder')}
                  options={copyOptionList}
                  value={dayToCopy}
                  onChange={(value) => handleCopy(value)}
                />
              </InlineGrid>
            </>
          )}
          <TextField
            label={t('visiblePickupDays.pickupPrepareTime.selectTimePrepare', {
              day:
                pickupSettings.pickupPreparationTimeType !== KindOfTimeType.SAME
                  ? `on ${pickupDayLabels[selectedWorkingDay?.day]}`
                  : '',
            })}
            value={selectedWorkingDay?.preparationTime?.value.toString()}
            onChange={(value) => {
              if (NUMBER_REGEX.test(value)) {
                dispatch(
                  handleSavePickupWeeklyWorkingDays(
                    pickupWeeklyWorkingDays.map((item) => {
                      if (item.day === selectedWorkingDay?.day) {
                        return {
                          ...item,
                          preparationTime: {
                            ...item.preparationTime,
                            value: value,
                          },
                        };
                      }
                      return item;
                    }),
                  ),
                );
              }
            }}
            onBlur={() => {
              if (isEmpty(selectedWorkingDay?.preparationTime?.value)) {
                dispatch(
                  handleSavePickupWeeklyWorkingDays(
                    pickupWeeklyWorkingDays.map((item) => {
                      if (item.day === selectedWorkingDay?.day) {
                        return {
                          ...item,
                          preparationTime: {
                            ...item.preparationTime,
                            value: DEFAULT_PREPARATION_TIME,
                          },
                        };
                      }
                      return item;
                    }),
                  ),
                );
              }
            }}
            autoComplete="off"
            type="text"
            connectedRight={
              <Select
                label=""
                options={timeUnitOptions}
                value={selectedWorkingDay?.preparationTime?.unit}
                onChange={(value: ITimeUnit['unit']) => {
                  dispatch(
                    handleSavePickupWeeklyWorkingDays(
                      pickupWeeklyWorkingDays.map((item) => {
                        if (item.day === selectedWorkingDay?.day) {
                          return {
                            ...item,
                            preparationTime: {
                              ...item.preparationTime,
                              unit: value,
                            },
                          };
                        }
                        return item;
                      }),
                    ),
                  );
                }}
              />
            }
          />
          <FormLayout>
            <InlineStack blockAlign="center" gap={'150'}>
              <Checkbox
                label={t('visiblePickupDays.pickupPrepareTime.cutOfTime')}
                checked={selectedWorkingDay?.isEnableCutOffTime}
                fill={false}
                onChange={(value) => {
                  dispatch(
                    handleSavePickupWeeklyWorkingDays(
                      pickupWeeklyWorkingDays.map((item) => {
                        if (item.day === selectedWorkingDay?.day) {
                          return {
                            ...item,
                            isEnableCutOffTime: value,
                          };
                        }
                        return item;
                      }),
                    ),
                  );
                }}
              />
              <Tooltip content={t('visiblePickupDays.pickupPrepareTime.cutOfTimeInstruction')} width="wide">
                <Box>
                  <Icon source={InfoIcon} />
                </Box>
              </Tooltip>
            </InlineStack>
          </FormLayout>
          {selectedWorkingDay?.isEnableCutOffTime && (
            <InlineGrid columns="1fr 5fr 1fr" gap="400" alignItems="start">
              <Select
                label={t('visiblePickupDays.pickupPrepareTime.cutOffTime')}
                options={timeOptions}
                value={selectedWorkingDay?.cutOffTime}
                onChange={(value) => {
                  dispatch(
                    handleSavePickupWeeklyWorkingDays(
                      pickupWeeklyWorkingDays.map((item) => {
                        if (item.day === selectedWorkingDay?.day) {
                          return {
                            ...item,
                            cutOffTime: value,
                          };
                        }
                        return item;
                      }),
                    ),
                  );
                }}
              />
              <TextField
                label={t('visiblePickupDays.pickupPrepareTime.preparationTime')}
                type="text"
                value={selectedWorkingDay?.cutOffPreparationTime?.value.toString()}
                onChange={(value) => {
                  if (NUMBER_REGEX.test(value)) {
                    dispatch(
                      handleSavePickupWeeklyWorkingDays(
                        pickupWeeklyWorkingDays.map((item) => {
                          if (item.day === selectedWorkingDay?.day) {
                            return {
                              ...item,
                              cutOffPreparationTime: {
                                ...item.cutOffPreparationTime,
                                value: value,
                              },
                            };
                          }
                          return item;
                        }),
                      ),
                    );
                  }
                }}
                onBlur={() => {
                  if (isEmpty(selectedWorkingDay?.cutOffPreparationTime?.value)) {
                    dispatch(
                      handleSavePickupWeeklyWorkingDays(
                        pickupWeeklyWorkingDays.map((item) => {
                          if (item.day === selectedWorkingDay?.day) {
                            return {
                              ...item,
                              cutOffPreparationTime: {
                                ...item.cutOffPreparationTime,
                                value: DEFAULT_CUT_OFF_TIME,
                              },
                            };
                          }
                          return item;
                        }),
                      ),
                    );
                  }
                }}
                autoComplete="off"
              />
              <Select
                label={'\u200B'}
                options={timeUnitOptions}
                onChange={(value: ITimeUnit['unit']) => {
                  dispatch(
                    handleSavePickupWeeklyWorkingDays(
                      pickupWeeklyWorkingDays.map((item) => {
                        if (item.day === selectedWorkingDay?.day) {
                          return {
                            ...item,
                            cutOffPreparationTime: {
                              ...item.cutOffPreparationTime,
                              unit: value,
                            },
                          };
                        }
                        return item;
                      }),
                    ),
                  );
                }}
                value={selectedWorkingDay?.cutOffPreparationTime?.unit}
              />
            </InlineGrid>
          )}
        </BlockStack>
      )}
    </BlockStack>
  );
};

export default PickupPrepareTime;
